// pages/POSPage.jsx — Mode caisse v3
import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../utils/auth';
import api from '../utils/api';
import { Search, ShoppingCart, Plus, Minus, Trash2, Check, X, Clock, Printer, Zap, AlertTriangle, Package, QrCode } from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
// Stock, Happy Hours et Commandes à table sont accessibles ici en onglets
// internes — c'est là qu'ils sont réellement utilisés au quotidien, pas
// besoin de leur propre ligne dans le menu.
import StockPage from './StockPage';
import AdminHappyHours from './admin/AdminHappyHours';
import TableOrdersPage from './TableOrdersPage';

const POS_TABS = [
  { key:'caisse',       label:'Caisse',             icon:ShoppingCart },
  { key:'stock',        label:'Stock',              icon:Package,  perm:'stock.view', feature:'stock' },
  { key:'happy',        label:'Happy Hours',        icon:Zap,      min:'admin' },
  { key:'table-orders', label:'Commandes à table',  icon:QrCode,   perm:'pos.access' },
];

// ── Hook détection mobile ────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 750);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 750);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return isMobile;
}

const productsApi = {
  list:       (siteId) => api.get('/stock', { params: siteId ? {siteId} : {} }),
  categories: () => api.get('/product-categories'),
  alerts:     () => api.get('/stock/alerts'),
};
// Vente atomique : tout le panier part en UN seul appel, qui décrémente le stock
// (en développant les combos sur leurs composants) et enregistre l'écriture de
// caisse dans la même transaction côté serveur (voir POST /stock/sell).
const salesApi = { create: (d) => api.post('/stock/sell', d) };

const LUCIDE_ICONS = {
  'banknotes':'💵','credit-card':'💳','document':'📄',
  'arrow-right':'➡️','gift':'🎁','cash':'💵','bank':'🏦',
};
function renderIcon(icon) {
  if (!icon) return '💰';
  if (/\p{Emoji}/u.test(icon)) return icon;
  return LUCIDE_ICONS[icon] || '💰';
}
function isCash(method, methods) {
  if (!method) return false;
  const m = methods.find(m => (m.id||m.name) === method);
  if (!m) return false;
  const n = (m.name||'').toLowerCase();
  return n.includes('espèce') || n.includes('cash') || n.includes('liquide') ||
    m.icon === 'banknotes' || m.icon === 'cash' || m.icon === '💵';
}

// ── Pavé numérique façon TPE ─────────────────────────────────
// Saisie en centimes : chaque chiffre tapé décale la virgule vers la gauche
// (ex: 6 → 0,06 · 0 → 0,60 · 0 → 6,00), comme sur un terminal de paiement.
// `cents` est un entier (le montant × 100) ; ⌫ retire le dernier chiffre.
function NumPad({ cents, onChange }) {
  const press = (k) => {
    if (k === '⌫') { onChange(Math.floor(cents / 10)); return; }
    const next = k === '00' ? cents * 100 : cents * 10 + parseInt(k, 10);
    if (next > 99999999) return; // garde-fou (999 999,99 €)
    onChange(next);
  };
  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:6 }}>
      {['7','8','9','4','5','6','1','2','3','00','0','⌫'].map(k => (
        <button key={k} onClick={() => press(k)}
          style={{
            padding:'14px 0', borderRadius:10,
            border: k === '⌫' ? '1px solid rgba(255,77,109,0.3)' : '1px solid var(--border)',
            background: k === '⌫' ? 'rgba(255,77,109,0.08)' : 'var(--bg-elevated)',
            color: k==='⌫' ? 'var(--red)' : 'var(--text-1)',
            fontSize:18, fontWeight:700, cursor:'pointer', fontFamily:'monospace',
          }}>
          {k}
        </button>
      ))}
    </div>
  );
}

function PayModal({ total, methods, onConfirm, onClose, completing }) {
  const [method, setMethod] = useState(methods[0]?.id || '');
  // Montant remis, stocké en centimes (entier) pour une saisie façon TPE
  // via le pavé numérique : chaque chiffre décale la virgule automatiquement.
  // `editedGiven` distingue le montant pré-rempli (total suggéré) d'une vraie
  // saisie : tant que l'utilisateur n'a pas encore tapé, le premier chiffre
  // repart de zéro au lieu de s'ajouter à la suggestion pré-remplie.
  const [givenCents, setGivenCents] = useState(Math.round(total * 100));
  const [editedGiven, setEditedGiven] = useState(false);
  const given  = (givenCents / 100).toFixed(2);
  const setSuggested = (c) => { setGivenCents(c); setEditedGiven(false); };
  const handleNumpadChange = (next) => { setGivenCents(next); setEditedGiven(true); };
  const cash   = isCash(method, methods);
  const change = givenCents/100 - total;

  // Entrée = valide / Échap = annule : géré génériquement pour toutes les
  // modales par <ModalHotkeys/> (clique le bouton "Valider" ci-dessous, qui
  // porte déjà exactement la même condition de désactivation) — plus besoin
  // d'un handler local ici.

  const quickAmounts = [...new Set([
    total,
    Math.ceil(total/5)*5,
    Math.ceil(total/10)*10,
    Math.ceil(total/20)*20,
  ])].filter(v => v >= total).slice(0,4);

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:440 }}>
        <div style={{ textAlign:'center', marginBottom:24 }}>
          <div style={{ fontSize:12, color:'var(--text-3)', marginBottom:4,
            textTransform:'uppercase', letterSpacing:'0.08em' }}>À encaisser</div>
          <div style={{ fontFamily:'monospace', fontSize:42, fontWeight:900, color:'var(--text-1)', lineHeight:1 }}>
            {total.toFixed(2)} <span style={{ fontSize:24, color:'var(--text-3)' }}>€</span>
          </div>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:`repeat(${Math.min(methods.length,3)},1fr)`, gap:8, marginBottom:20 }}>
          {methods.map(m => {
            const key = m.id || m.name;
            const sel = method === key;
            return (
              <button key={key}
                onClick={() => { setMethod(key); if (!isCash(key,methods)) setSuggested(Math.round(total*100)); }}
                style={{
                  padding:'14px 8px', borderRadius:12, cursor:'pointer', fontFamily:'inherit',
                  border:`2px solid ${sel ? 'var(--accent)' : 'var(--border)'}`,
                  background: sel ? 'rgba(124,92,252,0.15)' : 'var(--bg-elevated)',
                  color: sel ? 'var(--accent-light)' : 'var(--text-2)',
                  fontWeight:700, fontSize:12, display:'flex', flexDirection:'column',
                  alignItems:'center', gap:6, transition:'all 0.12s',
                  boxShadow: sel ? '0 0 0 1px var(--accent)' : 'none',
                }}>
                <span style={{ fontSize:22 }}>{renderIcon(m.icon)}</span>
                {m.name}
              </button>
            );
          })}
        </div>

        {cash && (
          <>
            <div style={{ background:'var(--bg-elevated)', borderRadius:14, padding:16, marginBottom:12 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                <span style={{ fontSize:12, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.06em' }}>
                  Remis
                </span>
                <div style={{ fontFamily:'monospace', fontSize:28, fontWeight:800 }}>{given} €</div>
              </div>
              {givenCents > 0 && (
                <div style={{
                  padding:'8px 12px', borderRadius:8, fontSize:15, fontWeight:700, textAlign:'center',
                  background: change >= 0 ? 'rgba(0,210,180,0.1)' : 'rgba(255,77,109,0.1)',
                  color: change >= 0 ? 'var(--teal)' : 'var(--red)',
                }}>
                  {change >= 0 ? `Monnaie : ${change.toFixed(2)} €` : `Manquant : ${Math.abs(change).toFixed(2)} €`}
                </div>
              )}
            </div>

            <div style={{ display:'flex', gap:6, marginBottom:12 }}>
              {quickAmounts.map(a => (
                <button key={a} onClick={() => setSuggested(Math.round(a*100))}
                  style={{
                    flex:1, padding:'8px 4px', borderRadius:8,
                    border:`1px solid ${givenCents===Math.round(a*100) ? 'var(--teal)' : 'var(--border)'}`,
                    background: givenCents===Math.round(a*100) ? 'rgba(0,210,180,0.1)' : 'var(--bg-elevated)',
                    color: givenCents===Math.round(a*100) ? 'var(--teal)' : 'var(--text-2)',
                    cursor:'pointer', fontFamily:'monospace', fontWeight:700, fontSize:13,
                  }}>
                  {a.toFixed(2)} €
                </button>
              ))}
            </div>
            <NumPad cents={editedGiven ? givenCents : 0} onChange={handleNumpadChange}/>
            <div style={{ height:16 }}/>
          </>
        )}

        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-secondary" style={{ flex:1, justifyContent:'center' }} onClick={onClose}>
            Annuler
          </button>
          <button className="btn btn-primary"
            style={{ flex:2, justifyContent:'center', fontSize:15, padding:'12px' }}
            onClick={() => onConfirm(method, givenCents/100)}
            disabled={completing || !method || (cash && givenCents/100 < total)}>
            {completing
              ? <span className="spinner" style={{width:16,height:16}}/>
              : <><Check size={15}/> Valider <span style={{ fontSize:11, opacity:0.6 }}>↵</span></>}
          </button>
        </div>
      </div>
    </div>
  );
}

function printReceipt({ cart, totalTTC, tvaBreakdown, methodName, change, cashGiven, businessName }) {
  const w = window.open('', '_blank', 'width=320,height=600');
  // item.unitPrice est déjà TTC (prix de vente final) — ne jamais le
  // remultiplier par la TVA, sous peine d'imprimer un total différent de ce
  // qui a réellement été encaissé.
  const lines = cart.map(i => `<tr>
      <td style="padding:2px 0">${i.product.name}${i.variant ? ` (${i.variant.name})` : ''}</td>
      <td style="text-align:center;padding:2px 6px">${i.qty}</td>
      <td style="text-align:right">${(i.unitPrice*i.qty).toFixed(2)} €</td>
    </tr>`).join('');
  const tvaLines = (tvaBreakdown||[]).filter(b => b.rate > 0).map(b => `
    <div style="display:flex;justify-content:space-between;color:#888;font-size:10px">
      <span>dont TVA ${b.rate}%</span><span>${b.tva.toFixed(2)} €</span></div>`).join('');
  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8">
  <style>* { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:monospace; font-size:12px; width:280px; padding:16px 8px; }
    h1 { font-size:15px; text-align:center; margin-bottom:2px; }
    .sub { text-align:center; color:#888; font-size:10px; margin-bottom:10px; }
    hr { border:none; border-top:1px dashed #ccc; margin:6px 0; }
    table { width:100%; }
    .total { font-weight:bold; font-size:14px; }
    .footer { text-align:center; font-size:10px; color:#aaa; margin-top:10px; }
  </style></head><body>
    <h1>${businessName||'Caisse'}</h1>
    <div class="sub">${format(new Date(),'dd/MM/yyyy HH:mm',{locale:fr})}</div>
    <hr>
    <table><tbody>${lines}</tbody></table>
    <hr>
    <div style="display:flex;justify-content:space-between" class="total">
      <span>TOTAL TTC</span><span>${totalTTC.toFixed(2)} €</span>
    </div>
    ${tvaLines}
    <hr>
    <div style="font-size:11px">Règlement : ${methodName}</div>
    ${cashGiven>0?`<div style="font-size:11px">Remis : ${cashGiven.toFixed(2)} €</div>`:''}
    ${change>0?`<div style="font-size:11px">Monnaie : ${change.toFixed(2)} €</div>`:''}
    <div class="footer">Merci !</div>
  </body></html>`);
  w.document.close();
  setTimeout(()=>{ w.focus(); w.print(); }, 300);
}

// ── Sélecteur de déclinaison (ex: parfum d'un diabolo) ─────────
function VariantPickerModal({ product, onPick, onClose }) {
  const baseTTC = product.happy_hour_active
    ? parseFloat(product.happy_hour_price)
    : (product.sell_price != null ? parseFloat(product.sell_price) : parseFloat(product.price || 0));
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:360 }}>
        <h3 className="modal-title">{product.icon || '📦'} {product.name} — {product.variant_group_label || 'Choix'}</h3>
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {(product.variants||[]).map(v => (
            <button key={v.id} type="button" className="btn btn-secondary"
              style={{ justifyContent:'space-between', padding:'12px 14px' }}
              onClick={() => onPick(v)}>
              <span>{v.name}</span>
              <span style={{ fontWeight:800, fontFamily:'monospace' }}>
                {(baseTTC + parseFloat(v.priceDelta||0)).toFixed(2)} €
              </span>
            </button>
          ))}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
        </div>
      </div>
    </div>
  );
}

function ProductCard({ product, inCart, onAdd, tvaRate, compact }) {
  const hhActive = !!product.happy_hour_active;
  // sell_price est le prix de vente TTC déjà défini dans la fiche produit —
  // ne JAMAIS le remultiplier par la TVA (ça facturerait plus cher que ce qui
  // est réellement encaissé côté serveur). On ne recalcule depuis le HT que
  // si aucun sell_price n'existe (donnée ancienne/incomplète).
  const basePriceTTC = product.sell_price != null
    ? parseFloat(product.sell_price)
    : (tvaRate > 0 ? parseFloat(product.price||0)*(1+tvaRate/100) : parseFloat(product.price||0));
  const priceTTC = hhActive ? parseFloat(product.happy_hour_price) : basePriceTTC;
  const stock    = product.stock_qty ?? product.quantity ?? null;
  const outOfStock = stock !== null && stock <= 0;
  const lowStock   = stock !== null && stock > 0 && stock <= (product.stock_alert_qty||3);
  const iconBox  = compact ? 36 : 44;
  const pad      = compact ? '10px 10px 10px' : '14px 12px 12px';

  return (
    <div onClick={() => outOfStock ? null : onAdd(product)}
      style={{
        position:'relative', borderRadius:14, cursor: outOfStock ? 'not-allowed' : 'pointer',
        opacity: outOfStock ? 0.4 : 1,
        border:`1.5px solid ${inCart ? 'var(--accent)' : 'var(--border)'}`,
        background: inCart
          ? 'linear-gradient(135deg, rgba(124,92,252,0.12) 0%, rgba(124,92,252,0.04) 100%)'
          : 'var(--bg-elevated)',
        transition:'transform 0.1s, border-color 0.1s',
        overflow:'hidden',
      }}
      onMouseEnter={e => { if (!outOfStock) { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.borderColor = inCart?'var(--accent)':'rgba(124,92,252,0.35)'; }}}
      onMouseLeave={e => { e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.borderColor = inCart?'var(--accent)':'var(--border)'; }}>

      {inCart > 0 && (
        <div style={{
          position:'absolute', top:8, right:8, width:22, height:22, borderRadius:'50%',
          background:'var(--accent)', color:'#fff', zIndex:1,
          fontSize:11, fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center',
          boxShadow:'0 2px 8px rgba(124,92,252,0.5)',
        }}>{inCart}</div>
      )}

      {lowStock && !hhActive && (
        <div style={{
          position:'absolute', top:0, left:0, right:0, height:3,
          background:'linear-gradient(90deg, var(--amber), transparent)',
          borderRadius:'14px 14px 0 0',
        }}/>
      )}

      {hhActive && (
        <div style={{
          position:'absolute', top:8, left:8, fontSize:9, fontWeight:800, color:'#fff',
          background:'var(--teal)', padding:'2px 7px', borderRadius:99, letterSpacing:'0.02em',
        }}>
          ⚡ HH
        </div>
      )}

      <div style={{ padding:pad }}>
        <div style={{
          fontSize: compact ? 22 : 28, marginBottom:8, width:iconBox, height:iconBox,
          display:'flex', alignItems:'center', justifyContent:'center',
          background:'var(--bg-base)', borderRadius:10,
        }}>
          {product.icon || product.emoji || '📦'}
        </div>

        <div style={{
          fontWeight:700, fontSize:12, marginBottom:2, lineHeight:1.35, color:'var(--text-1)',
          overflow:'hidden', textOverflow:'ellipsis',
          display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical',
          minHeight: compact ? 'auto' : 32,
        }}>
          {product.name}
        </div>
        {product.unit_label && (
          <div style={{ fontSize:10, color:'var(--text-3)', marginBottom:4 }}>{product.unit_label}</div>
        )}
        {(product.variants||[]).length > 0 && (
          <div style={{ fontSize:10, color:'var(--teal)', marginBottom:4, fontWeight:600 }}>
            {product.variant_group_label || 'Choix'} ▸
          </div>
        )}

        <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', gap:4, flexWrap:'wrap' }}>
          <span style={{ fontSize:16, fontWeight:900, color: hhActive ? 'var(--teal)' : inCart ? 'var(--accent-light)' : 'var(--text-1)' }}>
            {priceTTC.toFixed(2)} €
          </span>
          {hhActive ? (
            <span style={{ fontSize:11, color:'var(--text-3)', textDecoration:'line-through' }}>
              {basePriceTTC.toFixed(2)} €
            </span>
          ) : (tvaRate > 0 && <span style={{ fontSize:9, color:'var(--text-3)' }}>TTC</span>)}
        </div>

        {stock !== null && (
          <div style={{ marginTop:5, fontSize:10, fontWeight:600,
            color: outOfStock ? 'var(--red)' : lowStock ? 'var(--amber)' : 'var(--text-3)' }}>
            {outOfStock ? '● Rupture' : lowStock ? `⚠ ${stock} restant${stock>1?'s':''}` : `${stock} en stock`}
          </div>
        )}
      </div>
    </div>
  );
}

const QUICK_DISC = [
  { label:'-5%',  type:'pct',   value:'5'  },
  { label:'-10%', type:'pct',   value:'10' },
  { label:'-15%', type:'pct',   value:'15' },
  { label:'-1 €', type:'fixed', value:'1'  },
  { label:'-2 €', type:'fixed', value:'2'  },
  { label:'-5 €', type:'fixed', value:'5'  },
];

export default function POSPage() {
  const { can, features } = useAuth();
  const searchRef = useRef(null);
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();

  const visiblePosTabs = POS_TABS.filter(t => {
    if (t.feature && features && Object.keys(features).length > 0) {
      const feat = features[t.feature];
      if (feat && !feat.enabled) return false;
    }
    if (t.perm && !can(t.perm)) return false;
    if (t.min && !can(t.min)) return false;
    return true;
  });
  const [posTab, setPosTab] = useState(() => {
    const q = searchParams.get('tab');
    return visiblePosTabs.some(t => t.key === q) ? q : 'caisse';
  });

  const [products,    setProducts]  = useState([]);
  const [categories,  setCats]      = useState([]);
  const [filterCat,   setFilterCat] = useState('');
  const [search,      setSearch]    = useState('');
  const [methods,     setMethods]   = useState([]);
  const [sites,       setSites]     = useState([]);
  const [siteId,      setSiteId]    = useState('');
  const [cart,        setCart]      = useState([]);
  const [discType,    setDiscType]  = useState('none');
  const [discVal,     setDiscVal]   = useState('');
  const [showCustom,  setShowCustom]= useState(false);
  const [payModal,    setPayModal]  = useState(false);
  const [completing,  setComp]      = useState(false);
  const [lastSale,    setLastSale]  = useState(null);
  const [lastCart,    setLastCart]  = useState([]);
  const [lastTvaBreakdown, setLastTvaBreakdown] = useState([]);
  const [todaySales,  setTodaySales]= useState([]);
  const [showHist,    setShowHist]  = useState(false);
  const [tvaRate,     setTva]       = useState(0);
  const [businessName,setBizName]   = useState('');
  const [alerts,      setAlerts]    = useState([]);
  const [showAlerts,  setShowAlerts]= useState(false);
  const [allSitesCount, setAllSitesCount] = useState(null); // null = pas encore chargé
  const [variantPicker, setVariantPicker] = useState(null); // produit en cours de sélection de déclinaison

  // Clé unique d'une ligne de panier : un même produit avec deux déclinaisons
  // différentes occupe deux lignes distinctes.
  const cartKey = (productId, variantId) => variantId ? `${productId}::${variantId}` : productId;

  const loadAlerts = useCallback(async () => {
    try {
      const r = await productsApi.alerts();
      setAlerts(r.data.alerts || []);
    } catch { /* silencieux, non bloquant pour la caisse */ }
  }, []);

  const load = useCallback(async () => {
    try {
      const [prod, meths, sitesR, settingsR, cats] = await Promise.all([
        productsApi.list(siteId || undefined),
        api.get('/payments/methods').catch(() => ({data:{methods:[]}})),
        api.get('/sites').catch(() => ({data:{sites:[]}})),
        api.get('/settings').catch(() => ({data:{settings:[]}})),
        productsApi.categories().catch(() => ({data:{categories:[]}})),
      ]);
      const ps = (prod.data.products || prod.data.items || [])
        .filter(p => p.is_active !== false && p.is_for_sale !== false);
      setProducts(ps);
      setCats(cats.data.categories || cats.data || []);
      const ms = (meths.data.methods || []).filter(m => m.is_active !== false && m.is_pos !== false);
      setMethods(ms);
      const allSites = sitesR.data.sites || [];
      // Un site peut désactiver le mode caisse (ex: site sans bar/snack) —
      // il n'apparaît alors ni dans le sélecteur ni comme site sélectionnable.
      const ss = allSites.filter(s => s.pos_enabled !== false);
      setSites(ss);
      setAllSitesCount(allSites.length);
      if (ss.length === 1) setSiteId(ss[0].id);
      const arr = settingsR.data.settings || [];
      const get = k => (Array.isArray(arr) ? arr.find(s=>s.key===k)?.value : arr[k]) || '';
      setTva(parseFloat(get('tva_rate')||0));
      setBizName(get('business_name') || get('company_name') || '');
    } catch(e) { console.error(e); }
  }, [siteId]);

  useEffect(() => { load(); loadAlerts(); }, [load, loadAlerts]);

  // Rafraîchit les prix produits (bascule automatique des happy hours) sans
  // recharger toute la page — les prix changent tout seuls à l'heure dite.
  useEffect(() => {
    const refreshPrices = () => {
      productsApi.list(siteId || undefined).then(r => {
        const ps = (r.data.products || []).filter(p => p.is_active !== false && p.is_for_sale !== false);
        setProducts(ps);
      }).catch(() => {});
    };
    const t = setInterval(refreshPrices, 60000);
    return () => clearInterval(t);
  }, [siteId]);

  useEffect(() => {
    const h = (e) => {
      if (e.key === '/' && document.activeElement?.tagName !== 'INPUT') {
        e.preventDefault(); searchRef.current?.focus();
      }
      if (e.key === 'Enter' && !payModal && cart.length > 0 &&
          document.activeElement?.tagName !== 'INPUT' &&
          document.activeElement?.tagName !== 'BUTTON') {
        setPayModal(true);
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [payModal, cart]);

  // item.unitPrice est déjà TTC (voir addToCart) : subtotal est donc déjà un
  // montant TTC, il ne faut PLUS le multiplier par la TVA nulle part
  // ci-dessous — c'est exactement le bug qui gonflait le total affiché par
  // rapport à ce qui était réellement encaissé côté serveur.
  const subtotal = cart.reduce((s,i) => s + i.qty * i.unitPrice, 0);
  const discountAmount = discType === 'pct'
    ? subtotal * (parseFloat(discVal||0)/100)
    : discType === 'fixed'
      ? Math.min(parseFloat(discVal||0), subtotal)
      : 0;
  const totalTTC = Math.max(0, subtotal - discountAmount);

  // Ventilation TVA par taux, pour affichage/reçu uniquement (des produits
  // différents peuvent avoir des taux différents — alcools 20%, softs 10%…).
  // La remise est répartie au prorata entre les taux représentés au panier.
  const discountRatio = subtotal > 0 ? totalTTC / subtotal : 1;
  const tvaBreakdown = Object.values(
    cart.reduce((acc, i) => {
      const rate = i.tvaRate || 0;
      const adjTTC = i.qty * i.unitPrice * discountRatio;
      if (!acc[rate]) acc[rate] = { rate, ttc: 0 };
      acc[rate].ttc += adjTTC;
      return acc;
    }, {})
  ).map(g => ({
    rate: g.rate,
    ht:  g.rate > 0 ? g.ttc/(1+g.rate/100) : g.ttc,
    tva: g.rate > 0 ? g.ttc - g.ttc/(1+g.rate/100) : 0,
    ttc: g.ttc,
  })).sort((a,b) => b.rate - a.rate);

  // Taux de TVA effectif d'un produit : son propre taux, sinon celui de sa
  // catégorie, sinon le réglage général de l'établissement.
  const effectiveTvaRate = useCallback((product) => {
    if (product.tva_rate != null) return parseFloat(product.tva_rate);
    if (product.category_tva_rate != null) return parseFloat(product.category_tva_rate);
    return tvaRate;
  }, [tvaRate]);

  const addToCart = (product, variant = null) => {
    // Produit avec déclinaisons (ex: parfum) et aucune déclinaison précisée :
    // on ouvre le sélecteur au lieu d'ajouter directement au panier.
    if (!variant && Array.isArray(product.variants) && product.variants.length > 0) {
      setVariantPicker(product);
      return;
    }
    // sell_price est déjà TTC — ne pas le remultiplier par la TVA (voir
    // ProductCard). Le prix happy hour (si actif) prime automatiquement sur
    // le prix normal — plus besoin d'une touche caisse séparée "Produit HH".
    const baseTTC = product.happy_hour_active
      ? parseFloat(product.happy_hour_price)
      : (product.sell_price != null ? parseFloat(product.sell_price) : parseFloat(product.price || 0));
    const priceTTC = baseTTC + (variant ? parseFloat(variant.priceDelta || 0) : 0);
    const key = cartKey(product.id, variant?.id);
    setCart(prev => {
      const ex = prev.find(i => cartKey(i.product.id, i.variant?.id) === key);
      if (ex) return prev.map(i => cartKey(i.product.id,i.variant?.id)===key ? {...i,qty:i.qty+1} : i);
      return [...prev, {
        product, variant: variant || null, qty:1, unitPrice:priceTTC,
        isHappyHour: product.happy_hour_active,
        tvaRate: effectiveTvaRate(product),
      }];
    });
    setVariantPicker(null);
    // Sur mobile, revenir directement aux tuiles de catégories après l'ajout
    // évite d'avoir à cliquer manuellement sur "← Retour" à chaque produit.
    // On ne le fait pas pendant une recherche (l'utilisateur veut probablement
    // enchaîner sur un autre résultat de la même recherche).
    if (isMobile && !search && filterCat) setFilterCat('');
  };

  const updateQty = (key, delta) => {
    setCart(prev => prev.map(i => cartKey(i.product.id,i.variant?.id)===key
      ? {...i,qty:Math.max(0,i.qty+delta)} : i).filter(i=>i.qty>0));
  };

  const setQty = (key, v) => {
    const qty = Math.max(0, parseInt(v)||0);
    setCart(prev => qty===0
      ? prev.filter(i=>cartKey(i.product.id,i.variant?.id)!==key)
      : prev.map(i=>cartKey(i.product.id,i.variant?.id)===key ? {...i,qty} : i));
  };

  const completeSale = async (methodId, cashGiven) => {
    if (!cart.length) return;
    setComp(true);
    try {
      const methodObj = methods.find(m => (m.id||m.name)===methodId);
      const changeAmt = isCash(methodId,methods) ? Math.max(0, cashGiven-totalTTC) : 0;

      // Un seul appel atomique : décrémente le stock (combos développés sur leurs
      // composants côté serveur) ET enregistre l'écriture de caisse ensemble.
      await salesApi.create({
        items: cart.map(i => ({ productId: i.product.id, qty: i.qty, variantId: i.variant?.id || undefined })),
        siteId: siteId||null,
        paymentMethod: methodObj?.name || methodId,
        discountAmount: discountAmount || 0,
        notes: 'Vente POS',
      });

      const sale = {
        time:      format(new Date(),'HH:mm'),
        items:     cart.map(i=>`${i.qty}× ${i.product.name}${i.variant ? ` (${i.variant.name})` : ''}`).join(', '),
        total:     totalTTC,
        method:    methodObj?.name || methodId,
        change:    changeAmt,
        cashGiven: isCash(methodId,methods) ? cashGiven : 0,
      };
      setLastCart([...cart]);
      setLastTvaBreakdown(tvaBreakdown);
      setLastSale(sale);
      setTodaySales(prev=>[sale,...prev]);
      setCart([]); setDiscType('none'); setDiscVal('');
      setPayModal(false);
      toast.success(`${totalTTC.toFixed(2)} € encaissé`);
      loadAlerts();
    } catch(e) {
      toast.error(e.response?.data?.error || 'Erreur lors de la vente');
    } finally { setComp(false); }
  };

  const filteredProducts = products
    .filter(p => {
      if (!filterCat || filterCat === '__all__') return true;
      if (filterCat === '__none__') return !p.category_id;
      return String(p.category_id) === filterCat;
    })
    .filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()));

  const cartCount = cart.reduce((s,i)=>s+i.qty,0);

  if (!can('pos.access')) return (
    <div className="page-body"><div className="empty-state card"><p>Accès non autorisé</p></div></div>
  );

  // Tous les sites existants ont le mode caisse désactivé
  if (allSitesCount !== null && allSitesCount > 0 && sites.length === 0) return (
    <div className="page-body">
      <div className="empty-state card">
        <p>Le mode caisse est désactivé sur tous vos sites.</p>
        <p style={{ fontSize:12, color:'var(--text-3)', marginTop:6 }}>
          Activez-le depuis Gestion des sites → modifier un site.
        </p>
      </div>
    </div>
  );

  return (
    <>
      <div className="page-header">
        <div>
          <h2>Mode Caisse</h2>
          {posTab === 'caisse' && (
            <p style={{ display:'flex', alignItems:'center', gap:6 }}>
              Point de vente
              {[['/ chercher'],['↵ encaisser']].map(([s]) => (
                <span key={s} style={{ fontFamily:'monospace', fontSize:10, color:'var(--text-3)',
                  background:'var(--bg-elevated)', padding:'2px 8px', borderRadius:99 }}>{s}</span>
              ))}
            </p>
          )}
        </div>
        {posTab === 'caisse' && (
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            {alerts.length > 0 && (
              <button className={`btn btn-sm ${showAlerts?'btn-primary':'btn-secondary'}`}
                onClick={() => setShowAlerts(a=>!a)}
                style={{ gap:6, borderColor:'var(--amber)', color: showAlerts ? '#fff' : 'var(--amber)' }}
                title="Produits en alerte de stock">
                <AlertTriangle size={13}/>
                {alerts.length} en alerte
              </button>
            )}
            {todaySales.length > 0 && (
              <button className={`btn btn-sm ${showHist?'btn-primary':'btn-secondary'}`}
                onClick={() => setShowHist(h=>!h)} style={{ gap:6 }}>
                <Clock size={13}/>
                {todaySales.length} vente{todaySales.length>1?'s':''}
                · {todaySales.reduce((s,v)=>s+v.total,0).toFixed(2)} €
              </button>
            )}
            {sites.length > 1 && (
              <select className="input" style={{width:'auto'}} value={siteId}
                onChange={e=>setSiteId(e.target.value)}>
                <option value="">Tous les sites</option>
                {sites.map(s=><option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            )}
          </div>
        )}
      </div>

      {/* Onglets internes : Stock, Happy Hours et Commandes à table sont
          utilisés au même endroit que la caisse elle-même — inutile de leur
          garder une ligne de menu séparée. */}
      {visiblePosTabs.length > 1 && (
        <div style={{ display:'flex', gap:8, padding:'0 20px 14px', flexWrap:'wrap' }}>
          {visiblePosTabs.map(t => (
            <button key={t.key} type="button"
              className={`btn btn-sm ${posTab === t.key ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setPosTab(t.key)}>
              <t.icon size={13}/> {t.label}
            </button>
          ))}
        </div>
      )}

      {posTab === 'stock'        && <div className="page-body"><StockPage embedded/></div>}
      {posTab === 'happy'        && <div className="page-body"><AdminHappyHours embedded/></div>}
      {posTab === 'table-orders' && <div className="page-body"><TableOrdersPage embedded/></div>}

      {posTab === 'caisse' && (
      <div className="page-body">
        {/* Alertes stock */}
        {showAlerts && alerts.length > 0 && (
          <div className="card" style={{ marginBottom:16, borderColor:'var(--amber)',
            background:'rgba(245,166,35,0.05)' }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, color:'var(--amber)',
              fontWeight:600, fontSize:13, marginBottom:8 }}>
              <AlertTriangle size={15}/> {alerts.length} produit{alerts.length>1?'s':''} en alerte de stock
            </div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              {alerts.map(a => (
                <span key={a.id} style={{ fontSize:12, padding:'3px 10px', borderRadius:20,
                  background:'rgba(245,166,35,0.15)', color:'var(--amber)' }}>
                  {a.icon||'📦'} {a.name} ({a.stock_qty} restant{a.stock_qty>1?'s':''})
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Historique session */}
        {showHist && todaySales.length > 0 && (
          <div className="card" style={{ marginBottom:16, padding:0, overflow:'hidden' }}>
            <div style={{ padding:'10px 16px', borderBottom:'1px solid var(--border)',
              display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontWeight:700, fontSize:13 }}>Ventes de la session</span>
              <button onClick={() => setShowHist(false)}
                style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-3)', padding:2 }}>
                <X size={13}/>
              </button>
            </div>
            <div style={{ maxHeight:200, overflowY:'auto' }}>
              {todaySales.map((s,i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'8px 16px',
                  borderBottom:'1px solid var(--border)', fontSize:12 }}>
                  <span style={{ color:'var(--text-3)', fontFamily:'monospace', flexShrink:0 }}>{s.time}</span>
                  <span style={{ flex:1, color:'var(--text-2)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {s.items}
                  </span>
                  <span style={{ color:'var(--text-3)', fontSize:11, flexShrink:0 }}>{s.method}</span>
                  <span style={{ fontWeight:700, color:'var(--teal)', flexShrink:0 }}>{s.total.toFixed(2)} €</span>
                </div>
              ))}
            </div>
            <div style={{ padding:'8px 16px', display:'flex', justifyContent:'space-between',
              fontSize:13, fontWeight:700, background:'var(--bg-elevated)' }}>
              <span style={{ color:'var(--text-3)' }}>Total session</span>
              <span style={{ color:'var(--teal)' }}>{todaySales.reduce((s,v)=>s+v.total,0).toFixed(2)} €</span>
            </div>
          </div>
        )}

        {/* Bannière dernière vente */}
        {lastSale && (
          <div style={{ marginBottom:16, padding:'10px 16px', borderRadius:12,
            background:'rgba(0,210,180,0.06)', border:'1px solid rgba(0,210,180,0.3)',
            display:'flex', alignItems:'center', gap:12 }}>
            <span style={{ fontSize:18 }}>✅</span>
            <div style={{ flex:1, fontSize:13 }}>
              <strong style={{ color:'var(--teal)' }}>{lastSale.total.toFixed(2)} €</strong>
              <span style={{ color:'var(--text-3)', marginLeft:8, fontSize:12 }}>{lastSale.items}</span>
              {lastSale.change > 0 && (
                <span style={{ marginLeft:10, color:'var(--amber)', fontWeight:700 }}>
                  Monnaie : {lastSale.change.toFixed(2)} €
                </span>
              )}
            </div>
            <button className="btn btn-secondary btn-sm"
              onClick={() => printReceipt({
                cart: lastCart, totalTTC: lastSale.total, tvaBreakdown: lastTvaBreakdown,
                methodName: lastSale.method,
                change: lastSale.change,
                cashGiven: lastSale.cashGiven,
                businessName,
              })}>
              <Printer size={12}/> Reçu
            </button>
            <button onClick={() => setLastSale(null)}
              style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-3)', padding:4 }}>
              <X size={13}/>
            </button>
          </div>
        )}

        <div style={{ display:'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 340px',
          gap:20, alignItems:'start' }}>

          {/* Catalogue */}
          <div>
            <div style={{ position:'relative', marginBottom:12 }}>
              <Search size={14} style={{ position:'absolute', left:12, top:'50%',
                transform:'translateY(-50%)', color:'var(--text-3)', pointerEvents:'none' }}/>
              <input ref={searchRef} className="input"
                style={{ paddingLeft:36, paddingRight: search ? 36 : 12 }}
                value={search}
                onChange={e => { setSearch(e.target.value); if (e.target.value && !filterCat) setFilterCat('__all__'); }}
                placeholder="Rechercher un produit… (/)"/>
              {search && (
                <button onClick={()=>setSearch('')}
                  style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)',
                    background:'none', border:'none', cursor:'pointer', color:'var(--text-3)', padding:2 }}>
                  <X size={13}/>
                </button>
              )}
            </div>

            {/* Navigation catégories > produits */}
            {!search && categories.length > 0 ? (
              <>
                {/* Si aucune catégorie sélectionnée : tuiles catégories */}
                {!filterCat ? (
                  <div>
                    <div style={{ display:'grid', gridTemplateColumns:`repeat(auto-fit,minmax(${isMobile?150:120}px,1fr))`, gap:10, marginBottom:14 }}>
                      {/* Tuiles catégories — la tuile "Tout" a été retirée : elle
                          faisait doublon avec la recherche (qui bascule déjà sur
                          l'ensemble des produits dès qu'on tape) sans vraiment
                          apporter de valeur propre. */}
                      {categories.map(cat => {
                        const count = products.filter(p => String(p.category_id) === String(cat.id)).length;
                        return (
                          <div key={cat.id} onClick={() => setFilterCat(String(cat.id))}
                            style={{ borderRadius:14, border:'1px solid var(--border)', background:'var(--bg-elevated)',
                              padding: isMobile ? '18px 14px' : '12px 10px', cursor:'pointer', textAlign:'center', transition:'all 0.12s' }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(124,92,252,0.4)'; e.currentTarget.style.transform='translateY(-2px)'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.transform='translateY(0)'; }}>
                            <div style={{ fontSize:28, marginBottom:8 }}>{cat.icon || '📦'}</div>
                            <div style={{ fontWeight:700, fontSize:13 }}>{cat.name}</div>
                            <div style={{ fontSize:11, color:'var(--text-3)', marginTop:3 }}>
                              {count} produit{count>1?'s':''}
                            </div>
                          </div>
                        );
                      })}
                      {/* Produits sans catégorie */}
                      {products.filter(p => !p.category_id).length > 0 && (
                        <div onClick={() => setFilterCat('__none__')}
                          style={{ borderRadius:14, border:'1px solid var(--border)', background:'var(--bg-elevated)',
                            padding: isMobile ? '18px 14px' : '12px 10px', cursor:'pointer', textAlign:'center', transition:'all 0.12s' }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(124,92,252,0.4)'; e.currentTarget.style.transform='translateY(-2px)'; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.transform='translateY(0)'; }}>
                          <div style={{ fontSize:28, marginBottom:8 }}>🏷️</div>
                          <div style={{ fontWeight:700, fontSize:13 }}>Autres</div>
                          <div style={{ fontSize:11, color:'var(--text-3)', marginTop:3 }}>
                            {products.filter(p => !p.category_id).length} produit{products.filter(p=>!p.category_id).length>1?'s':''}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Produits de la catégorie sélectionnée */
                  <div>
                    <button onClick={() => setFilterCat('')}
                      className="btn btn-ghost btn-sm" style={{ marginBottom:12, gap:6 }}>
                      ← {filterCat === '__all__' ? 'Tous les produits' :
                          filterCat === '__none__' ? 'Autres' :
                          categories.find(c => String(c.id) === filterCat)?.name || 'Retour'}
                    </button>
                    {filteredProducts.length === 0 ? (
                      <div className="empty-state card"><p>Aucun produit dans cette catégorie</p></div>
                    ) : (
                      <div style={{ display:'grid', gridTemplateColumns:`repeat(auto-fit,minmax(${isMobile?140:110}px,1fr))`, gap:8 }}>
                        {filteredProducts.map(p => (
                          <ProductCard key={p.id} product={p}
                            inCart={cart.filter(i=>i.product.id===p.id).reduce((s,i)=>s+i.qty,0)}
                            onAdd={addToCart} tvaRate={tvaRate} compact={!isMobile}/>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              /* Pas de catégories ou recherche active : grille directe */
              filteredProducts.length === 0 ? (
                <div className="empty-state card">
                  <p>{search ? `Aucun résultat pour "${search}"` : 'Aucun produit disponible'}</p>
                </div>
              ) : (
                <div style={{ display:'grid', gridTemplateColumns:`repeat(auto-fit,minmax(${isMobile?140:110}px,1fr))`, gap:8 }}>
                  {filteredProducts.map(p => (
                    <ProductCard key={p.id} product={p}
                      inCart={cart.filter(i=>i.product.id===p.id).reduce((s,i)=>s+i.qty,0)}
                      onAdd={addToCart} tvaRate={tvaRate} compact={!isMobile}/>
                  ))}
                </div>
              )
            )}
          </div>

          {/* Panier */}
          <div style={isMobile ? undefined : { position:'sticky', top:20 }}>
            <div className="card" style={{ padding:0, overflow:'hidden' }}>

              <div style={{ padding:'12px 16px', borderBottom:'1px solid var(--border)',
                display:'flex', alignItems:'center', gap:8,
                background: cartCount > 0 ? 'rgba(124,92,252,0.05)' : 'transparent' }}>
                <ShoppingCart size={15} color={cartCount > 0 ? 'var(--accent-light)' : 'var(--text-3)'}/>
                <span style={{ fontWeight:700, fontSize:14 }}>Panier</span>
                {cartCount > 0 && (
                  <span style={{ fontSize:12, color:'var(--text-3)' }}>
                    {cartCount} article{cartCount>1?'s':''}
                  </span>
                )}
                {cart.length > 0 && (
                  <button className="btn btn-ghost btn-sm"
                    style={{ marginLeft:'auto', color:'var(--red)' }}
                    onClick={() => { setCart([]); setDiscType('none'); setDiscVal(''); }}>
                    <Trash2 size={11}/>
                  </button>
                )}
              </div>

              {cart.length === 0 ? (
                <div style={{ padding:'36px 16px', textAlign:'center' }}>
                  <ShoppingCart size={32} style={{ opacity:0.1, display:'block', margin:'0 auto 10px' }}/>
                  <div style={{ fontSize:13, color:'var(--text-3)' }}>Panier vide</div>
                  <div style={{ fontSize:11, color:'var(--text-3)', opacity:0.6, marginTop:4 }}>
                    Cliquez sur un produit
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ maxHeight:260, overflowY:'auto' }}>
                    {cart.map((item, idx) => {
                      const pttc = item.unitPrice; // déjà TTC — ne pas remultiplier
                      const key = cartKey(item.product.id, item.variant?.id);
                      return (
                        <div key={key} style={{
                          display:'flex', alignItems:'center', gap:8, padding:'9px 14px',
                          borderBottom: idx < cart.length-1 ? '1px solid var(--border)' : 'none',
                        }}>
                          <span style={{ fontSize:15, flexShrink:0 }}>{item.product.icon||'📦'}</span>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontWeight:600, fontSize:12, overflow:'hidden',
                              textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                              {item.product.name}
                              {item.variant && (
                                <span style={{ fontWeight:600, color:'var(--teal)', marginLeft:4 }}>
                                  · {item.variant.name}
                                </span>
                              )}
                              {item.product.unit_label && (
                                <span style={{ fontWeight:400, color:'var(--text-3)', marginLeft:4 }}>
                                  {item.product.unit_label}
                                </span>
                              )}
                              {item.isHappyHour && (
                                <span style={{ fontWeight:700, color:'var(--teal)', marginLeft:4, fontSize:10 }}>
                                  ⚡HH
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize:11, color:'var(--text-3)' }}>
                              {pttc.toFixed(2)} €
                            </div>
                          </div>
                          <div style={{ display:'flex', alignItems:'center', gap:4, flexShrink:0 }}>
                            <button onClick={() => updateQty(key,-1)}
                              style={{ width:26,height:26,borderRadius:7,border:'1px solid var(--border)',
                                background:'var(--bg-elevated)',color:'var(--text-1)',cursor:'pointer',
                                display:'flex',alignItems:'center',justifyContent:'center' }}>
                              <Minus size={12}/>
                            </button>
                            <input type="number" min={0} value={item.qty}
                              onChange={e=>setQty(key,e.target.value)}
                              style={{ width:32,textAlign:'center',background:'var(--bg-elevated)',
                                border:'1px solid var(--border)',borderRadius:5,color:'var(--text-1)',
                                fontSize:12,fontWeight:800,padding:'2px 0',fontFamily:'monospace' }}/>
                            <button onClick={() => updateQty(key,1)}
                              style={{ width:26,height:26,borderRadius:7,border:'1px solid var(--border)',
                                background:'var(--bg-elevated)',color:'var(--text-1)',cursor:'pointer',
                                display:'flex',alignItems:'center',justifyContent:'center' }}>
                              <Plus size={12}/>
                            </button>
                          </div>
                          <div style={{ fontWeight:700, fontSize:13, minWidth:48, textAlign:'right', flexShrink:0 }}>
                            {(item.qty*pttc).toFixed(2)} €
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Zone remise */}
                  <div style={{ padding:'10px 14px', borderTop:'1px solid var(--border)',
                    background:'var(--bg-elevated)' }}>
                    <div style={{ display:'flex', gap:5, flexWrap:'wrap', marginBottom: showCustom ? 8 : 0 }}>
                      {QUICK_DISC.map(d => {
                        const active = discType===d.type && discVal===d.value;
                        return (
                          <button key={d.label}
                            onClick={() => {
                              if (active) { setDiscType('none'); setDiscVal(''); }
                              else { setDiscType(d.type); setDiscVal(d.value); setShowCustom(false); }
                            }}
                            style={{
                              padding:'4px 9px', borderRadius:99, border:'none', cursor:'pointer',
                              fontFamily:'inherit', fontSize:11, fontWeight:700,
                              background: active ? 'var(--teal)' : 'var(--bg-base)',
                              color: active ? '#fff' : 'var(--text-3)',
                              transition:'all 0.1s',
                            }}>
                            {d.label}
                          </button>
                        );
                      })}
                      <button onClick={() => { setShowCustom(c=>!c); setDiscType('none'); setDiscVal(''); }}
                        style={{
                          padding:'4px 9px', borderRadius:99, border:'none', cursor:'pointer',
                          fontFamily:'inherit', fontSize:11, fontWeight:700,
                          background: showCustom ? 'var(--accent)' : 'var(--bg-base)',
                          color: showCustom ? '#fff' : 'var(--text-3)',
                        }}>
                        ✎
                      </button>
                    </div>

                    {showCustom && (
                      <div style={{ display:'flex', gap:6, alignItems:'center', marginTop:8 }}>
                        <input type="number" className="input" style={{ flex:1 }} min={0}
                          value={discVal} onChange={e=>setDiscVal(e.target.value)}
                          placeholder="Valeur"/>
                        <button
                          onClick={() => setDiscType('pct')}
                          style={{ padding:'6px 10px', borderRadius:8, border:'1px solid var(--border)',
                            background: discType==='pct' ? 'var(--accent)' : 'var(--bg-base)',
                            color: discType==='pct' ? '#fff' : 'var(--text-3)',
                            cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:700 }}>
                          %
                        </button>
                        <button
                          onClick={() => setDiscType('fixed')}
                          style={{ padding:'6px 10px', borderRadius:8, border:'1px solid var(--border)',
                            background: discType==='fixed' ? 'var(--accent)' : 'var(--bg-base)',
                            color: discType==='fixed' ? '#fff' : 'var(--text-3)',
                            cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:700 }}>
                          €
                        </button>
                      </div>
                    )}

                    {discountAmount > 0 && (
                      <div style={{ marginTop:6, fontSize:12, color:'var(--teal)', fontWeight:600,
                        display:'flex', justifyContent:'space-between' }}>
                        <span>Remise</span>
                        <span>-{discountAmount.toFixed(2)} €</span>
                      </div>
                    )}
                  </div>

                  {/* Totaux */}
                  <div style={{ padding:'12px 14px' }}>
                    {discountAmount > 0 && (
                      <div style={{ display:'flex', justifyContent:'space-between',
                        fontSize:12, color:'var(--text-3)', marginBottom:3 }}>
                        <span>Sous-total</span><span>{subtotal.toFixed(2)} €</span>
                      </div>
                    )}
                    {discountAmount > 0 && (
                      <div style={{ display:'flex', justifyContent:'space-between',
                        fontSize:12, color:'var(--teal)', marginBottom:3 }}>
                        <span>Remise</span><span>-{discountAmount.toFixed(2)} €</span>
                      </div>
                    )}
                    {tvaBreakdown.some(b => b.rate > 0) && (
                      <div style={{ marginBottom:6 }}>
                        {tvaBreakdown.map(b => (
                          <div key={b.rate} style={{ display:'flex', justifyContent:'space-between',
                            fontSize:11, color:'var(--text-3)' }}>
                            <span>dont TVA {b.rate > 0 ? `${b.rate}%` : '(exonéré)'}</span>
                            <span>{b.tva.toFixed(2)} €</span>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ display:'flex', justifyContent:'space-between',
                      alignItems:'baseline', fontWeight:900, marginBottom:12 }}>
                      <span style={{ fontSize:14, color:'var(--text-2)' }}>Total TTC</span>
                      <span style={{ fontSize:24, color:'var(--text-1)', fontFamily:'monospace' }}>
                        {totalTTC.toFixed(2)} €
                      </span>
                    </div>

                    <button className="btn btn-primary"
                      style={{ width:'100%', justifyContent:'center', padding:'13px',
                        fontSize:15, borderRadius:10, fontWeight:800 }}
                      onClick={() => setPayModal(true)}>
                      <Zap size={15}/> Encaisser
                      <span style={{ opacity:0.6, fontSize:12, marginLeft:6, fontWeight:400 }}>↵</span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      )}

      {payModal && (
        <PayModal
          total={totalTTC}
          methods={methods}
          completing={completing}
          onClose={() => setPayModal(false)}
          onConfirm={completeSale}
        />
      )}

      {variantPicker && (
        <VariantPickerModal
          product={variantPicker}
          onPick={(v) => addToCart(variantPicker, v)}
          onClose={() => setVariantPicker(null)}
        />
      )}
    </>
  );
}
